import { useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { useAuth } from '../auth/AuthContext'
import { useStandaloneMode } from '../pwa/useStandaloneMode'
import { useZoomDisabler } from '../pwa/useZoomDisabler'
import { IOSInstallBanner } from '../pwa/IOSInstallBanner'
import { usePreferencesSync } from './usePreferencesSync'
import { USER_NAV, ADMIN_NAV, EMPRESA_NAV_ITEM, SUSCRIPCION_NAV_ITEM, type NavConfig } from './navConfig'

type Props = {
  variant: 'user' | 'admin'
}

/**
 * Shell común para portal de usuario y portal de administrador.
 *
 * Cumple §5 SRS:
 *   - Sidebar con 3 zonas, colapsable y anclable (pin persistido por usuario).
 *   - Topbar con hamburguesa, breadcrumb y avatar.
 *   - Comportamiento distinto entre escritorio (≥768px) y móvil (<768px).
 *   - El portal admin tiene un sidebar/estado INDEPENDIENTE del portal usuario (§5.9).
 *
 * Cumple §3 SRS (PWA):
 *   - useZoomDisabler activo solo en modo standalone (REQ-PWA-005).
 *   - IOSInstallBanner se muestra solo en Safari iOS no instalado.
 *
 * El estado pin/colapso se mantiene local; usePreferencesSync lo persiste con debounce.
 */
export function AppShell({ variant }: Props) {
  const { state } = useAuth()
  const location = useLocation()
  const isStandalone = useStandaloneMode()

  // Estados independientes por portal (keys distintas en localStorage).
  const storageKey = variant === 'admin' ? 'intrepid.menu.admin' : 'intrepid.menu.user'

  const initialPinned = state.status === 'authenticated' ? state.user.menu_pinned : readPinned(storageKey)
  const [pinned, setPinned] = useState<boolean>(initialPinned)
  // desktopOpen: temporary expand state (session-only, not persisted). Starts open if pinned.
  const [desktopOpen, setDesktopOpen] = useState<boolean>(initialPinned)
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const [isDesktop, setIsDesktop] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches,
  )

  // Cerrar drawer mobile (y desktop temporal) cuando cambia la ruta.
  useEffect(() => {
    setMobileOpen(false)
    setDesktopOpen(false) // close temporary open; pinned sidebar stays via expanded = pinned || desktopOpen
  }, [location.pathname])

  // Reaccionar al cambio entre desktop/mobile.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Persistir el pin localmente (rápido) — la sincronización a backend la hace usePreferencesSync.
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, pinned ? '1' : '0')
    } catch {
      /* ignore */
    }
  }, [storageKey, pinned])

  // PWA hooks
  useZoomDisabler(isStandalone)
  usePreferencesSync({ menuPinned: pinned })

  // Para el portal de usuario, inyectar ítems exclusivos de owner si corresponde.
  let config: NavConfig = variant === 'admin' ? ADMIN_NAV : USER_NAV
  if (variant === 'user' && state.status === 'authenticated' && state.user.role === 'owner') {
    config = {
      ...USER_NAV,
      primary: [...USER_NAV.primary, EMPRESA_NAV_ITEM],
      middle: [SUSCRIPCION_NAV_ITEM],
    }
  }
  const pathToLabel = Object.fromEntries(
    [...config.primary, ...config.middle].map((item) => [item.path, item.label]),
  )
  const profilePath = variant === 'admin' ? '/admin/perfil' : '/perfil'
  const zoneLabel = variant === 'admin' ? 'Portal de administrador' : 'Portal de usuario'

  // pinned: anchored permanently (persisted). desktopOpen: temporarily expanded (session only).
  // En móvil el sidebar solo aparece si mobileOpen=true.
  const expanded = isDesktop ? (pinned || desktopOpen) : mobileOpen

  // Shared toggle logic for both sidebar hamburger and topbar hamburger on desktop.
  const handleDesktopToggle = () => {
    if (pinned) {
      // Closing a pinned sidebar: unpin and collapse.
      setPinned(false)
      setDesktopOpen(false)
    } else {
      setDesktopOpen((v) => !v)
    }
  }

  return (
    <div className="app-shell" data-variant={variant} data-pinned={pinned ? 'true' : 'false'}>
      <Sidebar
        config={config}
        expanded={expanded}
        mobileOpen={mobileOpen}
        pinned={pinned}
        onTogglePin={() => {
          setPinned((v) => {
            if (!v) setDesktopOpen(true) // ensure visible when anchoring
            return !v
          })
        }}
        onToggle={() => {
          if (isDesktop) handleDesktopToggle()
          else setMobileOpen((v) => !v)
        }}
        onNavigate={() => setMobileOpen(false)}
        zoneLabel={zoneLabel}
        hidePin={!isDesktop}
        profilePath={profilePath}
      />

      {/* Mobile: overlay oscuro */}
      {!isDesktop && mobileOpen && <div className="sidebar__overlay sidebar__overlay--mobile" onClick={() => setMobileOpen(false)} />}
      {/* Desktop: overlay transparente para cerrar sidebar temporal al hacer clic fuera */}
      {isDesktop && desktopOpen && !pinned && <div className="sidebar__overlay" onClick={() => setDesktopOpen(false)} />}

      <div className="app-shell__main">
        <Topbar
          onToggleMobileSidebar={() => {
            if (isDesktop) handleDesktopToggle()
            else setMobileOpen((v) => !v)
          }}
          pathToLabel={pathToLabel}
          profilePath={profilePath}
        />
        <main className="app-shell__content">
          <Outlet />
        </main>
      </div>

      <IOSInstallBanner />
    </div>
  )
}

function readPinned(key: string): boolean {
  if (typeof window === 'undefined') return false
  return window.localStorage.getItem(key) === '1'
}
